/**
 * What a sequence listing is. Most are shows ('sequence'); a few dozen are
 * timing tracks, effect presets, media packs or prop model files that live in
 * the same marketplace. One vocabulary for the classifier script, the Algolia
 * records, the search filter and the badge.
 *
 * No imports: bundled for the browser and imported by Vercel functions via
 * relative `.js` paths.
 */

export const LISTING_KINDS = ['sequence', 'timing_track', 'effect_preset', 'media_pack', 'prop_models'] as const;
export type ListingKind = (typeof LISTING_KINDS)[number];

/** Badge labels. 'sequence' has none: a plain sequence shows no badge. */
export const LISTING_KIND_LABELS: Record<Exclude<ListingKind, 'sequence'>, string> = {
  timing_track: 'Timing track',
  effect_preset: 'Effect preset',
  media_pack: 'Media pack',
  prop_models: 'Prop models',
};

/** Type filter options, in display order. */
export const LISTING_KIND_FILTER_OPTIONS: ReadonlyArray<{ value: ListingKind; label: string }> = [
  { value: 'sequence', label: 'Sequences' },
  { value: 'timing_track', label: 'Timing tracks' },
  { value: 'effect_preset', label: 'Effect presets' },
  { value: 'media_pack', label: 'Media packs' },
  { value: 'prop_models', label: 'Prop models' },
];

export function isListingKind(value: unknown): value is ListingKind {
  return typeof value === 'string' && (LISTING_KINDS as readonly string[]).includes(value);
}

/** The stored value if it is a known kind, else 'sequence'. For records built from rows. */
export function normalizeListingKind(value: unknown): ListingKind {
  return isListingKind(value) ? value : 'sequence';
}

/** Badge text for a record's listing_kind, or null when no badge should show. */
export function listingKindLabel(value: unknown): string | null {
  if (!isListingKind(value) || value === 'sequence') return null;
  return LISTING_KIND_LABELS[value];
}

/**
 * Default Algolia filter for sequence search, shelves and profile tabs.
 * Written as exclusions, not `listing_kind:sequence`, so records that have not
 * been re-indexed with the field yet still count as sequences.
 */
export const REAL_SEQUENCES_ALGOLIA_FILTER = LISTING_KINDS.filter((k) => k !== 'sequence')
  .map((k) => `NOT listing_kind:${k}`)
  .join(' AND ');

const SHOW_EXTENSIONS = new Set([
  'xsq', 'xsqz', 'fseq', // xLights / FPP
  'lms', 'las', 'loredit', 'lcc', 'lcs', // Light-O-Rama
  'seq', 'piz', 'vsa', // other show formats the upload accepts or sees in the wild
]);
const MEDIA_EXTENSIONS = new Set([
  'png', 'jpg', 'jpeg', 'gif', 'mp4', 'mov', 'mp3', 'wav', 'psd', 'ai', 'eps', 'pdn', 'svg',
]);
const IGNORED_EXTENSIONS = new Set(['txt', 'pdf']);
const IGNORED_NAMES = new Set(['.ds_store', 'xlights_networks.xml', 'xlights_keybindings.xml']);

function extensionOf(base: string): string {
  const dot = base.lastIndexOf('.');
  return dot <= 0 ? '' : base.slice(dot + 1);
}

/**
 * Classify a listing from the file names inside its package. Show files win;
 * otherwise timing, presets, models, then media-only packs. Anything else,
 * including an empty or unreadable list, stays a 'sequence'.
 */
export function classifyPackageListing(entryNames: readonly string[]): ListingKind {
  const exts: string[] = [];
  for (const raw of entryNames) {
    const name = raw.replace(/\\/g, '/');
    if (name.endsWith('/')) continue;
    const parts = name.split('/');
    if (parts.includes('__MACOSX')) continue;
    const base = (parts[parts.length - 1] ?? '').toLowerCase();
    if (!base || IGNORED_NAMES.has(base)) continue;
    if (/^readme(\.|$)/.test(base) || /^read me\./.test(base)) continue;
    const ext = extensionOf(base);
    if (IGNORED_EXTENSIONS.has(ext)) continue;
    exts.push(ext);
  }
  if (exts.length === 0) return 'sequence';
  if (exts.some((e) => SHOW_EXTENSIONS.has(e))) return 'sequence';
  if (exts.includes('xtiming')) return 'timing_track';
  if (exts.includes('xpreset')) return 'effect_preset';
  if (exts.includes('xmodel')) return 'prop_models';
  if (exts.every((e) => MEDIA_EXTENSIONS.has(e))) return 'media_pack';
  return 'sequence';
}

/**
 * Algolia partial-update objects ({ objectID, listing_kind }) for sequence rows.
 * Skips rows with an unknown kind rather than guessing, and returns them so the
 * caller can report them.
 */
export function buildListingKindUpdates(
  rows: ReadonlyArray<{ id: string; listing_kind: unknown }>,
): { updates: Array<{ objectID: string; listing_kind: ListingKind }>; skipped: string[] } {
  const updates: Array<{ objectID: string; listing_kind: ListingKind }> = [];
  const skipped: string[] = [];
  for (const row of rows) {
    if (isListingKind(row.listing_kind)) updates.push({ objectID: row.id, listing_kind: row.listing_kind });
    else skipped.push(row.id);
  }
  return { updates, skipped };
}
